import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReactComponent as BackIcon } from '../../assets/icons/backIcon.svg';

import { loader } from 'graphql.macro';
import { useLazyQuery } from '@apollo/client';
import {
    formatDuration,
    formatToDoubleDigit,
    formatToQuadrupleDigit,
    numberToDollarFormat,
    roundDown,
    roundDown4Digits,
    formatDateTime,
} from '../../utils/utility';
import { StripeTransactionTypes } from '../../utils/enums';

const getStripeTransaction = loader('./getStripeTransaction.graphql');

const App: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [accountBalance, setAccountBalance] = useState(0);
    const [amount, setAmount] = useState(0);
    const [title, setTitle] = useState('');
    const [duration, setDuration] = useState('');
    const [minutes, setMinutes] = useState(0);
    const [startingBalance, setStartingBalance] = useState<number | null>(null);
    const [endingBalance, setEndingBalance] = useState<number | null>(null);
    const [total, setTotal] = useState(0);
    const [totalkWh, setTotalkWh] = useState(0);
    const [date, setDate] = useState('');
    const [ccFee, setCCFee] = useState<number | null>(null);
    const [sessionFee, setSessionFee] = useState<number | null>(null);
    const [minFee, setMinFee] = useState<number | null>(null);
    const [baseCost, setBaseCost] = useState<number | null>(null);
    const [sessionTotal, setSessionTotal] = useState(0);
    const [delayKwh, setDelayKwh] = useState(0);
    const [getData, { loading, error, data }] = useLazyQuery(
        getStripeTransaction,
        { fetchPolicy: 'no-cache' }
    );
    const [name, setName] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [transactionId, setTransactionId] = useState<string | null>(null);
    const [fixedSessionFee, setFixedSessionFee] = useState(0);
    const [percentageSessionFee, setPercentageSessionFee] = useState(0);
    const [powerLoss, setPowerLoss] = useState<string | null>(null);
    const [sessionFeeString, setSessionFeeString] = useState('');
    const [sessionDurationString, setSessionDurationString] = useState('');
    // const [stationKw, setStationKw] = useState<number | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const queryParams: Record<string, string> = {};
        params.forEach((value, key) => {
            queryParams[key] = value;
        });
        setTitle(queryParams?.type || '');
        if (queryParams?.type === StripeTransactionTypes.SESSION) {
            getData({ variables: { filter: queryParams?.tId } });
        }
        setTotal(parseInt(queryParams?.total));
        if (queryParams?.eBalance) {
            setEndingBalance(parseInt(queryParams?.eBalance));
            setStartingBalance(
                parseInt(queryParams?.total) - parseInt(queryParams?.eBalance)
            );
        }
        if (queryParams?.date) {
            setDate(queryParams.date);
        }
        if (queryParams?.base) {
            setBaseCost(parseInt(queryParams.base) / 100);
        }
        if (queryParams?.fee) {
            setSessionFee(parseInt(queryParams?.fee) / 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (data && data.ocppSession) {
            const endTime = new Date(data.ocppSession.disconnectedAt);
            const startTime = new Date(data.ocppSession.connectedAt);
            setName('Chargie');
            setAddress(data.ocppSession.property.address1);
            setCity(data.ocppSession.property.city);
            setState(data.ocppSession.property.state);
            setZip(data.ocppSession.property.zip);
            setTransactionId(data.ocppSession.ocppTransactionId);
            setDuration(
                formatDuration(endTime.getTime() - startTime.getTime())
            );
            setMinutes(
                Math.floor((endTime.getTime() - startTime.getTime()) / 60000)
            );
            if (
                data.ocppSession.ocppSubSessions &&
                data.ocppSession.driverPricing?.policy === 'TOU'
            ) {
                setTotalkWh(
                    data.ocppSession.ocppSubSessions.reduce(
                        (acc: number, item: any) => acc + item.totalWh / 1000,
                        0
                    )
                );
                setAmount(
                    data.ocppSession.ocppSubSessions.reduce(
                        (acc: number, item: any) =>
                            acc + (item.cost + item.serviceAmount) / 100,
                        0
                    )
                );
            } else {
                setTotalkWh(data.ocppSession.wh / 1000);
                setAmount(
                    data.ocppSession.amount + data.ocppSession.serviceAmount
                );
            }
            if (data.ocppSession.powerLossAt) {
                setPowerLoss(
                    'Power loss: ' +
                        formatDateTime(data.ocppSession.powerLossAt)
                );
            }
            // // Calculate kW from cluster's maxVoltage * maxAmps / 1000
            // if (
            //     data.ocppSession.station?.cluster?.maxVoltage &&
            //     data.ocppSession.station?.cluster?.maxAmps
            // ) {
            //     const calculatedKw =
            //         (data.ocppSession.station.cluster.maxVoltage *
            //             data.ocppSession.station.cluster.maxAmps) /
            //         1000;
            //     setStationKw(calculatedKw);
            // } else {
            //     setStationKw(null);
            // }
            if (
                data.ocppSession.connectedAt &&
                data.ocppSession.disconnectedAt
            ) {
                setSessionDurationString(
                    `${formatDateTime(data.ocppSession.connectedAt)} - ${formatDateTime(data.ocppSession.disconnectedAt)}`
                );
            }
            let calculatedTotal = 0;

            if (data.ocppSession?.driverPricing?.startDelay > 0) {
                setDelayKwh(
                    Number(
                        (
                            (data.ocppSession.delayedMeterStart -
                                data.ocppSession.meterStart || 0) / 1000
                        ).toFixed(3)
                    )
                );
            }

            if (
                data.ocppSession.adjustmentProcessingFee &&
                data.ocppSession.adjustmentProcessingFee.amount
            ) {
                setCCFee(data.ocppSession.adjustmentProcessingFee.amount);
            }
            if (
                data.ocppSession.adjustmentSessionFee &&
                data.ocppSession.adjustmentSessionFee.amount
            ) {
                setSessionFee(data.ocppSession.adjustmentSessionFee.amount);
                calculatedTotal += data.ocppSession.adjustmentSessionFee.amount;
            }
            if (
                data.ocppSession.adjustmentMinFee &&
                data.ocppSession.adjustmentMinFee.amount
            ) {
                setMinFee(data.ocppSession.adjustmentMinFee.amount);
            }
            if (
                data.ocppSession.adjustmentDiscount &&
                data.ocppSession.adjustmentDiscount.amount
            ) {
                calculatedTotal += data.ocppSession.adjustmentDiscount.amount;
            }
            if (
                data.ocppSession.adjustmentDiscountCSF &&
                data.ocppSession.adjustmentDiscountCSF.amount
            ) {
                calculatedTotal +=
                    data.ocppSession.adjustmentDiscountCSF.amount;
            }
            if (
                data.ocppSession.adjustmentPenalty &&
                data.ocppSession.adjustmentPenalty.amount
            ) {
                calculatedTotal += data.ocppSession.adjustmentPenalty.amount;
            }
            if (data.ocppSession.sessionFee) {
                if (
                    data.ocppSession.sessionFee.fixed > 0 &&
                    data.ocppSession.sessionFee.percentage > 0
                ) {
                    setSessionFeeString(`
                        ${data.ocppSession.sessionFee.percentage * 100}% + ${numberToDollarFormat(data.ocppSession.sessionFee.fixed)}`);
                } else if (data.ocppSession.sessionFee.fixed > 0) {
                    setSessionFeeString(
                        `${numberToDollarFormat(data.ocppSession.sessionFee.fixed)}`
                    );
                } else if (data.ocppSession.sessionFee.percentage > 0) {
                    setSessionFeeString(
                        `${data.ocppSession.sessionFee.percentage * 100}%`
                    );
                }
            }
            setSessionTotal(calculatedTotal);
        }
    }, [data]);

    const createLineItem = () => {
        if (data && data.ocppSession) {
            if (data.ocppSession.driverPricing?.policy === 'TOU') {
                return (
                    <div>
                        {data.ocppSession.ocppSubSessions.map((item: any) => {
                            // Need to use the original pricing policy to get the policy name
                            let policyName = '';
                            for (const policy of data.ocppSession.pricingPolicy
                                .jsonData.policy) {
                                for (const phase of policy.phases) {
                                    if (
                                        Math.round(Number(phase.rate) * 100) ===
                                            item.rate &&
                                        item.phaseKey === phase.name
                                    ) {
                                        policyName = policy.name;
                                        break;
                                    }
                                }
                            }
                            // Use the policy name to get the adjusted rate from the driver pricing
                            const rate =
                                data.ocppSession.driverPricing?.jsonData?.policy
                                    ?.find(
                                        (policy: any) =>
                                            policy.name === policyName
                                    )
                                    ?.phases.find(
                                        (phase: any) =>
                                            phase.name === item.phaseKey
                                    )?.rate;

                            return (
                                <div className="pricing-detail-item">
                                    <div
                                        className="pricing-detail-item-header"
                                        id="payment_detail_tou_header"
                                    >
                                        <div id="payment_detail_tou_header_kwh">
                                            TOU Charging cost
                                        </div>
                                        <div id="payment_detail_tou_cost">
                                            {numberToDollarFormat(
                                                (item.cost +
                                                    item.serviceAmount) /
                                                    100
                                            )}
                                        </div>
                                    </div>
                                    <div
                                        className="pricing-detail-item-subheader"
                                        id="payment_detail_tou_subheader"
                                    >
                                        {formatToQuadrupleDigit(
                                            item.totalWh / 1000
                                        )}
                                        kWh @{' '}
                                        {numberToDollarFormat(
                                            rate ?? item.rate / 100
                                        )}
                                        /kWh
                                    </div>
                                    <div className="pricing-detail-item-subheader">
                                        {formatDateTime(item.startAt)} -{' '}
                                        {formatDateTime(item.stopAt)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                );
            } else if (data.ocppSession.driverPricing?.policy === 'HOUR') {
                return (
                    <div className="pricing-detail-item">
                        <div
                            className="pricing-detail-item-header"
                            id="payment_detail_hour_header"
                        >
                            <div id="payment_detail_header_hour_cost">
                                Charging cost
                            </div>
                            <div id="payment_detail_hour_cost">
                                {numberToDollarFormat(
                                    (data.ocppSession.amount || 0) +
                                        (data.ocppSession.serviceAmount || 0)
                                )}
                            </div>
                        </div>
                        <div
                            className="pricing-detail-item-subheader"
                            id="payment_detail_hour_subheader"
                        >
                            {duration} @{' '}
                            {numberToDollarFormat(
                                data.ocppSession.driverPricing?.rate || 0
                            )}
                            /hr
                        </div>
                        <div className="pricing-detail-item-subheader">
                            {formatToQuadrupleDigit(data.ocppSession.wh / 1000)}
                            kWh
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className="pricing-detail-item">
                        <div
                            className="pricing-detail-item-header"
                            id="payment_detail_kwh_header"
                        >
                            <div id="payment_detail_kwh_kwh">Charging cost</div>
                            <div id="payment_detail_kwh_cost">
                                {numberToDollarFormat(
                                    (data.ocppSession.amount || 0) +
                                        (data.ocppSession.serviceAmount || 0)
                                )}
                            </div>
                        </div>
                        <div
                            className="pricing-detail-item-subheader"
                            id="payment_detail_kwh_subheader"
                        >
                            {formatToQuadrupleDigit(
                                data.ocppSession.wh / 1000 - delayKwh
                            )}
                            kWh @{' '}
                            {numberToDollarFormat(
                                data.ocppSession.driverPricing?.rate || 0
                            )}
                            /kWh
                        </div>
                        <div className="pricing-detail-item-subheader" id="">
                            {formatDateTime(data.ocppSession.connectedAt)} -{' '}
                            {formatDateTime(data.ocppSession.disconnectedAt)}
                        </div>
                    </div>
                );
            }
        }
        return <></>;
    };

    const createDiscountItem = () => {
        if (
            data &&
            data.ocppSession &&
            (data.ocppSession.adjustmentDiscount ||
                data.ocppSession.adjustmentDiscountCSF)
        ) {
            if (data.ocppSession.guestSession) {
                return <></>;
            } else if (data.ocppSession.groupDiscount) {
                return (
                    <div>
                        <div
                            className="pricing-detail-item-header pricing-detail-border"
                            style={{ paddingTop: 10 }}
                            id="payment_detail_discount"
                        >
                            <div id="payment_detail_discount_header">
                                Discount
                            </div>

                            <div id="payment_detail_discount_cost">
                                (
                                {numberToDollarFormat(
                                    (data.ocppSession.adjustmentDiscount
                                        ?.amount || 0) +
                                        (data.ocppSession.adjustmentDiscountCSF
                                            ?.amount || 0)
                                )}
                                )
                            </div>
                        </div>
                        <div className="pricing-detail-item-subheader">
                            {data.ocppSession.groupDiscount.domainName}{' '}
                            {data.ocppSession.groupDiscount.discountRate * 100}%
                            OFF
                        </div>
                        <div className="pricing-detail-item-subheader">
                            {sessionDurationString}
                        </div>
                    </div>
                );
            }
        }
    };

    const createDelayItem = () => {
        if (
            data &&
            data.ocppSession &&
            data.ocppSession.driverPricing?.startDelay &&
            data.ocppSession.driverPricing?.startDelay > 0
        ) {
            return (
                <div className="pricing-detail-item" id="payment_detail_delay">
                    <div
                        className="pricing-detail-item-header"
                        id="payment_detail_delay_header"
                    >
                        <div id="payment_detail_delay_kwh">
                            {formatToQuadrupleDigit(
                                (data.ocppSession.delayedMeterStart -
                                    data.ocppSession.meterStart || 0) / 1000
                            )}
                            kWh @ Free
                        </div>
                        <div id="payment_detail_delay_cost">
                            {numberToDollarFormat(0)}
                        </div>
                    </div>
                    <div
                        className="pricing-detail-item-subheader"
                        id="payment_detail_subheader"
                    >
                        {roundDown(
                            (data.ocppSession.driverPricing?.startDelay || 0) /
                                60
                        )}
                        hr free
                    </div>
                </div>
            );
        }
        return <></>;
    };

    const createPenaltyItem = () => {
        if (data && data.ocppSession && data.ocppSession.adjustmentPenalty) {
            const adjustment = data.ocppSession.adjustmentPenalty.amount;
            const rate = data.ocppSession.driverPricing?.penaltyRate | 1;
            const delay = data.ocppSession.driverPricing?.penaltyDelay | 0;

            return (
                <div
                    className="pricing-detail-item"
                    id="payment_detail_penalty"
                >
                    <div
                        className="pricing-detail-item-header"
                        id="payment_detail_penalty_header"
                    >
                        <div id="payment_detail_penalty_kwh">
                            Penalty Fee
                            {/* {formatToDoubleDigit(Math.ceil(adjustment / rate))}
                            :00 @{' '}
                            {numberToDollarFormat(
                                data.ocppSession.driverPricing?.penaltyRate || 0
                            )}
                            /hr */}
                        </div>
                        <div id="payment_detail_penalty_cost">
                            {numberToDollarFormat(adjustment)}
                        </div>
                    </div>
                    <div
                        className="pricing-detail-item-subheader"
                        id="payment_detail_penalty_subheader"
                    >
                        Penalty fee{' '}
                        {numberToDollarFormat(
                            data.ocppSession.driverPricing?.penaltyRate || 0
                        )}
                        /hr, after {roundDown(delay / 60)}{' '}
                        {delay / 60 > 1 ? 'hrs' : 'hr'}
                    </div>
                </div>
            );
        }
        return <></>;
    };

    const createSummaryDetails = () => {
        if (
            title === StripeTransactionTypes.AUTO ||
            title === StripeTransactionTypes.RELOAD
        ) {
            return (
                <div>
                    <div
                        className="pricing-detail-item"
                        id="payment_detail_reload"
                    >
                        <div
                            className="pricing-detail-item-header"
                            id="payment_detail_reload_header"
                        >
                            <div id="payment_detail_reload_title">
                                Account reload
                            </div>
                            <div id="payment_detail_reload_cost">
                                {numberToDollarFormat(total / -100)}
                            </div>
                        </div>
                    </div>
                    {ccFee && ccFee > 0 ? (
                        <div className="pricing-detail-item pricing-detail-border">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_reload_fee">
                                    Processing fee
                                </div>

                                <div id="payment_detail_reload_fee_amount">
                                    {numberToDollarFormat(ccFee)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                    {sessionFee && sessionFee > 0 ? (
                        <div className="pricing-detail-item pricing-detail-border">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_reload_fee">
                                    Processing fee
                                </div>

                                <div id="payment_detail_reload_fee_amount">
                                    {numberToDollarFormat(sessionFee)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                    <div className="pricing-detail-item pricing-detail-border">
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_reload_cost">
                                Transaction total
                            </div>
                            <div id="payment_detail_reload_cost_amount">
                                {numberToDollarFormat(
                                    total / -100 + (ccFee || 0)
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else if (
            [
                StripeTransactionTypes.DEBIT,
                StripeTransactionTypes.CREDIT,
                StripeTransactionTypes.REFUND,
            ].includes(title as StripeTransactionTypes)
        ) {
            return (
                <div>
                    <div
                        className="pricing-detail-item"
                        id="payment_detail_refund"
                    >
                        <div
                            className="pricing-detail-item-header"
                            id="payment_detail_refund_header"
                        >
                            <div id="payment_detail_refund_title">
                                {title === StripeTransactionTypes.REFUND
                                    ? 'Amount refunded'
                                    : title}
                            </div>
                            <div id="payment_detail_refund_amount">
                                {numberToDollarFormat(Math.abs(total / 100))}
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else if (
            startingBalance &&
            endingBalance &&
            endingBalance / -100 > 0
        ) {
            return (
                <div>
                    <div
                        className="pricing-detail-item"
                        style={{ paddingBottom: 5 }}
                    >
                        <div
                            className="pricing-detail-item-header"
                            id="payment_detail_balance"
                        >
                            <div id="payment_detail_balance_title">
                                Starting balance
                            </div>
                            <div id="payment_detail_balance_amount">
                                {numberToDollarFormat(startingBalance / 100)}
                            </div>
                        </div>
                    </div>
                    <div
                        className="pricing-detail-item pricing-detail-border"
                        style={{ paddingTop: 5, paddingBottom: 5 }}
                    >
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_balance_session_total">
                                Amount owed
                            </div>
                            <div id="payment_detail_balance_session_total_amount">
                                {numberToDollarFormat(
                                    -(baseCost || total / 100)
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        className="pricing-detail-item pricing-detail-border"
                        style={{ paddingTop: 5, paddingBottom: 0 }}
                    >
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_ending">Ending balance</div>
                            <div id="payment_detail_ending_amount">
                                {numberToDollarFormat(endingBalance / -100)}
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else if (startingBalance && endingBalance) {
            return (
                <div>
                    <div
                        className="pricing-detail-item"
                        style={{ paddingBottom: 5 }}
                    >
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_start">
                                Starting balance
                            </div>
                            <div id="payment_detail_start_amount">
                                {numberToDollarFormat(startingBalance / 100)}
                            </div>
                        </div>
                    </div>
                    <div
                        className="pricing-detail-item pricing-detail-border"
                        style={{ paddingTop: 5, paddingBottom: 5 }}
                    >
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_sess_total">
                                Amount owed
                            </div>
                            <div id="payment_detail_sess_total_amount">
                                {numberToDollarFormat(
                                    -(baseCost || total / 100)
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        className="pricing-detail-item pricing-detail-border"
                        style={{ paddingTop: 5, paddingBottom: 0 }}
                    >
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_outstanding">
                                Ending balance
                            </div>
                            <div id="payment_detail_outstanding_amount">
                                {endingBalance / -100 === 0
                                    ? numberToDollarFormat(0)
                                    : numberToDollarFormat(
                                          endingBalance / -100
                                      )}
                            </div>
                        </div>
                    </div>
                    {ccFee && ccFee > 0 ? (
                        <div className="pricing-detail-item">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_fee">
                                    Processing fee
                                </div>
                                <div id="payment_detail_fee_amount">
                                    {numberToDollarFormat(ccFee * -1)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                    {ccFee && ccFee > 0 ? (
                        <div className="pricing-detail-item">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_fee">
                                    Processing fee
                                </div>
                                <div id="payment_detail_fee_amount">
                                    {numberToDollarFormat(ccFee * -1)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                    {minFee && minFee > 0 ? (
                        <div className="pricing-detail-item">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_min_fee">
                                    $0.50 minimum purchase
                                </div>
                                <div id="payment_detail_min_fee_amount">
                                    {numberToDollarFormat(minFee * -1)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                </div>
            );
        } else {
            // For PAYG sessions, skip Amount owed and Transaction total
            const isPaygSession = data?.ocppSession?.sessionType === 'PAYG';

            if (isPaygSession) {
                return <></>;
            }

            return (
                <div>
                    <div className="pricing-detail-item">
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_ses_total">Amount owed</div>
                            <div id="payment_detail_ses_total_amount">
                                {numberToDollarFormat(
                                    -(baseCost || total / 100)
                                )}
                            </div>
                        </div>
                    </div>
                    {/* {sessionFee && (
                        <div className="pricing-detail-item">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_process_fee">
                                    Session Fee
                                </div>
                                <div id="payment_detail_process_fee_amount">
                                    {numberToDollarFormat(sessionFee || 0)}
                                </div>
                            </div>
                        </div>
                    )} */}
                    {ccFee && (
                        <div className="pricing-detail-item">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_process_fee">
                                    Processing fee
                                </div>
                                <div id="payment_detail_process_fee_amount">
                                    {numberToDollarFormat(ccFee || 0)}
                                </div>
                            </div>
                        </div>
                    )}
                    {minFee && (
                        <div className="pricing-detail-item">
                            <div className="pricing-detail-item-header">
                                <div id="payment_detail_minfee">
                                    $0.50 minimum purchase
                                </div>
                                <div id="payment_detail_minfee_amount">
                                    {numberToDollarFormat(minFee)}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="pricing-detail-item pricing-detail-border">
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_due">Transaction total</div>
                            <div id="payment_detail_due_amount">
                                {numberToDollarFormat(
                                    total / 100 + (minFee || 0) + (ccFee || 0)
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <motion.div
            className="container"
            initial={
                location.state && location.state.slide === true
                    ? {
                          marginLeft: '100%',
                          width: '100%',
                          transformOrigin: 'left',
                      }
                    : {}
            }
            animate={{ marginLeft: 0 }}
            id="payment_detail"
        >
            <div className="header">
                <div className="header__content">
                    <div
                        className="header__back"
                        onClick={() => {
                            navigate(-1);
                        }}
                        id="payment_detail_back"
                    >
                        <BackIcon id="payment_detail_icon"></BackIcon>
                    </div>
                    {title && title.toUpperCase()}
                </div>
            </div>
            <div className="content" id="payment_detail_content">
                {data && (
                    <div className="payment-detail-station">
                        <div className="payment-detail-station-header">
                            <div id="payment_detail_station">
                                Station {data.ocppSession.station.qrCode} - Plug{' '}
                                {data.ocppSession.connector} |{' '}
                                {(data.ocppSession.maxKw ?? 7.68).toFixed(2)}kW
                                AC
                            </div>
                            {/* <div id="payment_detail_total_kwh">
                                {formatToQuadrupleDigit(totalkWh / 1000)} kWh
                            </div> */}
                        </div>
                        <div
                            className="payment-detail-subheader"
                            style={{ marginBottom: 20 }}
                        >
                            <div>{name}</div>
                            <div>{address}</div>
                            {city !== '' && (
                                <div>
                                    {city}, {state} {zip}
                                </div>
                            )}
                            {powerLoss && <div>{powerLoss}</div>}
                        </div>
                        <div className="payment-detail-bar"></div>
                        <div>Session details</div>
                        {transactionId && (
                            <div className="payment-detail-station-subheader">
                                <div>ID: {transactionId}</div>
                            </div>
                        )}
                        {/* <div className="payment-detail-station-subheader">
                            {data.ocppSession?.driverPricing?.policy ===
                            'TOU' ? (
                                <div>Time-of-use rates</div>
                            ) : (
                                <div></div>
                            )}
                            <div>{duration}</div>
                        </div> */}
                        {createDelayItem()}
                        {createLineItem()}

                        {data.ocppSession.driverPricing?.policy === 'TOU' && (
                            <>
                                <div
                                    className="pricing-detail-item-header pricing-detail-border"
                                    style={{ paddingTop: 20, marginTop: 20 }}
                                >
                                    <div>Session total</div>
                                    <div>{numberToDollarFormat(amount)}</div>
                                </div>
                                <div className="pricing-detail-item-subheader">
                                    {formatToQuadrupleDigit(totalkWh)} kWh
                                </div>
                            </>
                        )}
                    </div>
                )}
                {!data && !loading && !error && (
                    <div className="payment-detail-station">
                        {[
                            StripeTransactionTypes.DEBIT,
                            StripeTransactionTypes.CREDIT,
                            StripeTransactionTypes.REFUND,
                        ].includes(title as StripeTransactionTypes) && (
                            <div className="payment-detail-station">
                                <div className="payment-detail-station-header">
                                    <div>{title}</div>
                                    <div>
                                        {numberToDollarFormat(
                                            Math.abs(total / 100)
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {(title.toUpperCase() === 'AUTO-RELOAD' ||
                            title.toUpperCase() === 'RELOAD') && (
                            <div>
                                <div
                                    className="payment-detail-station-header"
                                    style={{ marginBottom: 30 }}
                                >
                                    <div>Added funds</div>
                                    <div>
                                        {numberToDollarFormat(total / -100)}
                                    </div>
                                </div>
                                <div className="pricing-detail-item">
                                    <div className="pricing-detail-item-header">
                                        <div>Starting balance</div>
                                        <div>
                                            {numberToDollarFormat(
                                                (startingBalance || 0) / 100
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="pricing-detail-item ">
                                    <div className="pricing-detail-item-header">
                                        <div>Reload amount</div>
                                        <div>
                                            {numberToDollarFormat(total / -100)}
                                        </div>
                                    </div>
                                </div>
                                <div className="pricing-detail-item pricing-detail-border">
                                    <div className="pricing-detail-item-header">
                                        <div>Ending balance</div>
                                        <div>
                                            {(endingBalance || 0) / -100 === 0
                                                ? numberToDollarFormat(0)
                                                : numberToDollarFormat(
                                                      (endingBalance || 0) /
                                                          -100
                                                  )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className="payment-detail-bar"></div>
                <div className="payment-detail-station-header">
                    Transaction details
                </div>
                <div className="payment-detail-station-subheader">{date}</div>
                {createDiscountItem()}
                {createPenaltyItem()}
                {sessionFee && sessionFee > 0 ? (
                    <div className="pricing-detail-item">
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_reload_fee">
                                Session/Activation fee
                            </div>

                            <div id="payment_detail_reload_fee_amount">
                                {numberToDollarFormat(sessionFee)}
                            </div>
                        </div>
                        <div className="pricing-detail-item-subheader">
                            {sessionFeeString}
                        </div>
                        <div className="pricing-detail-item-subheader" id="">
                            {sessionDurationString}
                        </div>
                    </div>
                ) : (
                    <div></div>
                )}
                {minFee && minFee > 0 && (
                    <div className="pricing-detail-item">
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_min_fee">
                                $0.50 minimum purchase
                            </div>
                            <div id="payment_detail_min_fee_amount">
                                {numberToDollarFormat(minFee * 1)}
                            </div>
                        </div>
                    </div>
                )}

                {data && data.ocppSession && (
                    <div
                        className="pricing-detail-item pricing-detail-border"
                        style={{ marginBottom: 40 }}
                    >
                        <div className="pricing-detail-item-header">
                            <div id="payment_detail_transaction_total">
                                Transaction total
                            </div>
                            <div id="payment_detail_transaction_total_amount">
                                {numberToDollarFormat(
                                    baseCost || total / 100 + (minFee || 0)
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {createSummaryDetails()}
            </div>
        </motion.div>
    );
};

export default App;
